import React from 'react';
import { EdgeLabelRenderer, useReactFlow, type EdgeProps } from '@xyflow/react';
import InteractiveWaypoints from './InteractiveWaypoints';

interface RoutePoint {
  x: number;
  y: number;
  type: 'start' | 'waypoint' | 'end';
}

interface EdgeLabelData {
  id: string;
  text: string;
  position: number;
}

interface CustomEdgeData {
  connectionId: number;
  pathPoints: RoutePoint[];
  isManualRoute: boolean;
  wireProperties: any;
  onWaypointUpdate?: (edgeId: string, pathPoints: RoutePoint[]) => void;
  edgeLabels?: EdgeLabelData[];
  onEdgeLabelsUpdate?: (edgeId: string, labels: EdgeLabelData[]) => void;
  isReadOnly?: boolean;
}

const clampLabelPosition = (value: number): number => Math.min(0.95, Math.max(0.05, value));

const getDistance = (a: { x: number; y: number }, b: { x: number; y: number }): number => (
  Math.hypot(b.x - a.x, b.y - a.y)
);

const getPointAtRatio = (points: Array<{ x: number; y: number }>, ratio: number): { x: number; y: number } => {
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1) return points[0];

  const targetDistance = points.slice(1).reduce((sum, point, index) => sum + getDistance(points[index], point), 0) * clampLabelPosition(ratio);
  let walked = 0;

  for (let i = 0; i < points.length - 1; i += 1) {
    const start = points[i];
    const end = points[i + 1];
    const segmentLength = getDistance(start, end);
    if (segmentLength === 0) continue;

    if (walked + segmentLength >= targetDistance) {
      const localRatio = (targetDistance - walked) / segmentLength;
      return {
        x: start.x + (end.x - start.x) * localRatio,
        y: start.y + (end.y - start.y) * localRatio,
      };
    }

    walked += segmentLength;
  }

  return points[points.length - 1];
};

const getRatioForPoint = (points: Array<{ x: number; y: number }>, point: { x: number; y: number }): number => {
  if (points.length < 2) return 0.5;

  const totalLength = points.slice(1).reduce((sum, current, index) => sum + getDistance(points[index], current), 0);
  if (totalLength === 0) return 0.5;

  let bestDistance = Number.POSITIVE_INFINITY;
  let bestWalked = 0;
  let walked = 0;

  for (let i = 0; i < points.length - 1; i += 1) {
    const start = points[i];
    const end = points[i + 1];
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const segmentLengthSquared = dx * dx + dy * dy;

    if (segmentLengthSquared === 0) continue;

    const rawT = ((point.x - start.x) * dx + (point.y - start.y) * dy) / segmentLengthSquared;
    const t = Math.min(1, Math.max(0, rawT));
    const projected = {
      x: start.x + dx * t,
      y: start.y + dy * t,
    };
    const distanceToProjection = getDistance(point, projected);

    if (distanceToProjection < bestDistance) {
      bestDistance = distanceToProjection;
      bestWalked = walked + Math.sqrt(segmentLengthSquared) * t;
    }

    walked += Math.sqrt(segmentLengthSquared);
  }

  return clampLabelPosition(bestWalked / totalLength);
};

const CustomEdge: React.FC<EdgeProps> = (props) => {
  // console.log('🔵 CUSTOM EDGE RENDERING!', props.id, 'selected:', props.selected);
  
  const edgeData = props.data as CustomEdgeData | undefined;
  const { screenToFlowPosition } = useReactFlow();
  const pathPoints = edgeData?.pathPoints || [];
  const isSelected = props.selected;
  
  // Get wire properties
  const wireProps = edgeData?.wireProperties || {};
  
  // Edge type from wire properties - NEVER change based on waypoints
  const edgeTypeRaw = wireProps.type as string | undefined;
  const edgeType = edgeTypeRaw || 'smoothstep';
  
  // Get current waypoints - either from live editing, from database, or auto-generate
  const currentWaypoints = React.useMemo(() => {
    const existingWaypoints = (pathPoints || []).filter(p => p.type === 'waypoint');
    
    // Auto-generate waypoints for smoothstep/step when selected and no waypoints exist
    if (isSelected && (edgeType === 'smoothstep' || edgeType === 'step') && existingWaypoints.length === 0) {
      // Generate default L-shaped waypoints
      const highestY = Math.min(props.sourceY, props.targetY) - 30;
      return [
        { x: props.sourceX, y: highestY, type: 'waypoint' as const },
        { x: props.targetX, y: highestY, type: 'waypoint' as const }
      ];
    }
    
    return existingWaypoints;
  }, [pathPoints, isSelected, edgeType, props.sourceX, props.sourceY, props.targetX, props.targetY]);
  
  // console.log('🔵 CustomEdge data:', { id: props.id, edgeType, wireProps, pathPoints: pathPoints.length });
  
    // Handle waypoint updates
  const handleWaypointUpdate = (newWaypoints: RoutePoint[]) => {
    // console.log('🔵 Waypoint updated:', newWaypoints);
    
    // Call parent callback if provided
    if (edgeData?.onWaypointUpdate) {
      edgeData.onWaypointUpdate(props.id, newWaypoints);
    } else {
      console.warn('🔵 No onWaypointUpdate callback provided');
    }
  };
  
  // Always render full custom edge when selected
  
  // Generate path string based on edge type and waypoints
  const generatePath = () => {
    // Use waypoints only to parameterize the shape; keep visual style by type
    const wp = currentWaypoints.length > 0 ? currentWaypoints : pathPoints.filter(p => p.type === 'waypoint');

    if (edgeType === 'default' || edgeType === 'bezier') {
      const controlX = (props.sourceX + props.targetX) / 2;
      // Control point should create a nice curve - offset to the side
      const deltaY = Math.abs(props.targetY - props.sourceY);
      const controlY = (props.sourceY + props.targetY) / 2 + deltaY * 0.3;
      return `M ${props.sourceX} ${props.sourceY} Q ${controlX} ${controlY} ${props.targetX} ${props.targetY}`;
    }

    if (edgeType === 'smoothstep') {
      const radius = 10;
      // Use waypoints to parameterize the smoothstep shape
      let midY: number;
      if (wp.length >= 2) {
        // Use the horizontal waypoint (second one) to control midY
        midY = wp[1].y;
      } else if (wp.length === 1) {
        // Single waypoint controls midY
        midY = wp[0].y;
      } else {
        // No waypoints - use default calculation
        const highestY = Math.min(props.sourceY, props.targetY);
        midY = highestY - 30;
      }
      let path = `M ${props.sourceX} ${props.sourceY}`;
      if (Math.abs(props.sourceY - midY) > radius) {
        if (props.sourceY > midY) {
          path += ` L ${props.sourceX} ${midY + radius}`;
          path += ` Q ${props.sourceX} ${midY} ${props.sourceX + radius} ${midY}`;
        } else {
          path += ` L ${props.sourceX} ${midY - radius}`;
          path += ` Q ${props.sourceX} ${midY} ${props.sourceX + radius} ${midY}`;
        }
      } else {
        path += ` L ${props.sourceX} ${midY}`;
      }
      if (Math.abs(props.sourceX - props.targetX) > radius) {
        if (props.sourceX < props.targetX) {
          path += ` L ${props.targetX - radius} ${midY}`;
          if (props.targetY < midY) {
            path += ` Q ${props.targetX} ${midY} ${props.targetX} ${midY - radius}`;
          } else {
            path += ` Q ${props.targetX} ${midY} ${props.targetX} ${midY + radius}`;
          }
        } else {
          path += ` L ${props.targetX + radius} ${midY}`;
          if (props.targetY < midY) {
            path += ` Q ${props.targetX} ${midY} ${props.targetX} ${midY - radius}`;
          } else {
            path += ` Q ${props.targetX} ${midY} ${props.targetX} ${midY + radius}`;
          }
        }
      } else {
        path += ` L ${props.targetX} ${midY}`;
      }
      path += ` L ${props.targetX} ${props.targetY}`;
      return path;
    }

    if (edgeType === 'step') {
      // Use waypoints to parameterize the step shape
      let cornerY: number;
      if (wp.length >= 2) {
        // Use the horizontal waypoint (second one) to control cornerY
        cornerY = wp[1].y;
      } else if (wp.length === 1) {
        // Single waypoint controls cornerY
        cornerY = wp[0].y;
      } else {
        // No waypoints - use default
        cornerY = props.targetY;
      }
      const cornerX = props.sourceX;
      return `M ${props.sourceX} ${props.sourceY} L ${cornerX} ${cornerY} L ${props.targetX} ${props.targetY}`;
    }

    // Straight or unknown
    if (edgeType === 'straight' && wp.length > 0) {
      // For straight lines with waypoints, create a polyline through them
      let path = `M ${props.sourceX} ${props.sourceY}`;
      for (const waypoint of wp) {
        path += ` L ${waypoint.x} ${waypoint.y}`;
      }
      path += ` L ${props.targetX} ${props.targetY}`;
      return path;
    }
    return `M ${props.sourceX} ${props.sourceY} L ${props.targetX} ${props.targetY}`;
  };
  
  const pathString = generatePath();
  
  // Get wire properties
  const strokeColor = (props.style?.stroke as string | undefined) || wireProps.color || '#3b82f6';
  const strokeWidth = (props.style?.strokeWidth as number | undefined) || wireProps.stroke_width || 3;
  const strokeDasharray = (props.style?.strokeDasharray as string | undefined) || wireProps.stroke_dasharray;
  const edgeLabels: EdgeLabelData[] = edgeData?.edgeLabels || wireProps.edge_labels || wireProps.edgeLabels || [];
  const labelRoutePoints = React.useMemo(
    () => [
      { x: props.sourceX, y: props.sourceY },
      ...currentWaypoints.map((point) => ({ x: point.x, y: point.y })),
      { x: props.targetX, y: props.targetY },
    ],
    [currentWaypoints, props.sourceX, props.sourceY, props.targetX, props.targetY]
  );

  const updateLabelPosition = React.useCallback((labelId: string, clientX: number, clientY: number) => {
    if (!edgeData?.onEdgeLabelsUpdate || edgeData.isReadOnly) return;

    const flowPoint = screenToFlowPosition({ x: clientX, y: clientY });
    const nextPosition = getRatioForPoint(labelRoutePoints, flowPoint);
    const nextLabels = edgeLabels.map((label) => (
      label.id === labelId ? { ...label, position: nextPosition } : label
    ));

    edgeData.onEdgeLabelsUpdate(props.id, nextLabels);
  }, [edgeData, edgeLabels, labelRoutePoints, props.id, screenToFlowPosition]);

  const handleLabelPointerDown = React.useCallback((event: React.PointerEvent<HTMLDivElement>, labelId: string) => {
    if (!edgeData?.onEdgeLabelsUpdate || edgeData.isReadOnly) return;

    event.preventDefault();
    event.stopPropagation();

    const handlePointerMove = (moveEvent: PointerEvent) => {
      updateLabelPosition(labelId, moveEvent.clientX, moveEvent.clientY);
    };
    const handlePointerUp = () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  }, [edgeData, updateLabelPosition]);

  return (
    <g
      role="button"
      aria-label={`Связь от ${props.source} к ${props.target}`}
      data-edge-id={props.id}
      tabIndex={0}
      focusable="true"
    >
      {/* Main path */}
      <path
        d={pathString}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
        fill="none"
        className="react-flow__edge-path"
        style={{ cursor: 'pointer', stroke: strokeColor, strokeWidth: strokeWidth, strokeDasharray: strokeDasharray }}
      />
      
      {/* Interaction area for hover effects */}
      <path
        d={pathString}
        stroke="transparent"
        strokeWidth="8"
        fill="none"
        className="react-flow__edge-interaction"
      />
      
      {/* Debug markers removed - were used for testing anchor points visualization */}
      {/* <circle cx={props.sourceX} cy={props.sourceY} r={2} fill="#ef4444" opacity="0.6" /> */}
      {/* <circle cx={props.targetX} cy={props.targetY} r={2} fill="#22c55e" opacity="0.6" /> */}

      {/* Interactive waypoints - use currentWaypoints which includes auto-generated ones */}
      <InteractiveWaypoints
        pathPoints={currentWaypoints}
        isSelected={isSelected || false}
        onWaypointUpdate={handleWaypointUpdate}
        sourceX={props.sourceX}
        sourceY={props.sourceY}
        targetX={props.targetX}
        targetY={props.targetY}
        edgeType={wireProps.type}
      />
      
      {/* Selection indicator */}
      {isSelected && (
        <path
          d={pathString}
          stroke="#3b82f6"
          strokeWidth={strokeWidth + 4}
          fill="none"
          opacity="0.3"
          strokeDasharray="5,5"
          style={{ pointerEvents: 'none' }}
        />
      )}

      {edgeLabels.length > 0 && (
        <EdgeLabelRenderer>
          {edgeLabels
            .filter((label) => label.text.trim().length > 0)
            .map((label) => {
              const labelPoint = getPointAtRatio(labelRoutePoints, label.position ?? 0.5);
              return (
                <div
                  key={label.id}
                  className="nodrag nopan"
                  onPointerDown={(event) => handleLabelPointerDown(event, label.id)}
                  style={{
                    position: 'absolute',
                    transform: `translate(-50%, -50%) translate(${labelPoint.x}px, ${labelPoint.y}px)`,
                    pointerEvents: edgeData?.isReadOnly ? 'none' : 'all',
                    cursor: edgeData?.isReadOnly ? 'default' : 'grab',
                    zIndex: 1001,
                  }}
                  title={edgeData?.isReadOnly ? label.text : 'Перетащите метку вдоль линии'}
                >
                  <div className="rounded border border-gray-300 bg-white/95 px-1.5 py-0.5 text-xs font-medium text-gray-900 shadow-sm">
                    {label.text}
                  </div>
                </div>
              );
            })}
        </EdgeLabelRenderer>
      )}
    </g>
  );
};

CustomEdge.displayName = 'CustomEdge';

export default CustomEdge;
